import { ToastAction, ToastEvent, ToastId } from "./toast-service";

/** How many toasts are on screen at once; older ones wait, mounted and invisible, for a free slot. */
export const TOAST_VISIBLE_LIMIT: number = 3;

/** A closed toast is kept this long for its exit animation, then dropped on the next show. */
export const TOAST_CLOSED_RETENTION_MS: number = 1000;

export interface IToastItem extends ToastEvent {
  open: boolean;
  /** `Date.now()` when it closed; unset while open. */
  closedAt?: number;
}

export interface IToastStackLayout {
  /** 0 is the newest open toast, in front. */
  index: number;
  /** Past the visible window: rendered invisible, its timer waits. */
  isHidden: boolean;
  /** Collapsed: how many steps this card peeks out behind the front one (0 for the front one). */
  peekSteps: number;
  /** Expanded: heights of the newer visible cards below this one, in px. */
  offset: number;
  /** The card's drawn height in px; unset until the card has been measured. */
  height?: number;
}

export interface IToastStack {
  layouts: Map<ToastId, IToastStackLayout>;
  /** The viewport's height in px, not counting gaps. */
  contentHeight: number;
  /** How many `--gap-s` gaps the viewport adds to `contentHeight`. */
  gapCount: number;
}

/**
 * The provider's state transition for one service action. Pure: `now` is passed in.
 * - show: appends (oldest first, as the DOM always had them); an id already present is replaced in
 *   place. Toasts closed longer than `TOAST_CLOSED_RETENTION_MS` ago are dropped.
 * - update: merges into an open toast; a closed or unknown id is ignored so a late result cannot
 *   resurrect a toast the user already dismissed.
 * - dismiss: closes an open toast; its exit animation still plays.
 * Returns `items` itself when nothing changes.
 */
export const applyToastAction = (items: IToastItem[], action: ToastAction, now: number): IToastItem[] => {
  if (action.kind === "show") {
    const kept: IToastItem[] = items.filter(
      (item: IToastItem): boolean =>
        item.open || item.closedAt === undefined || now - item.closedAt < TOAST_CLOSED_RETENTION_MS
    );
    const existing: number = kept.findIndex((item: IToastItem): boolean => item.id === action.toast.id);
    if (existing < 0) {
      return [...kept, { ...action.toast, open: true }];
    }
    return kept.map((item: IToastItem, index: number): IToastItem =>
      index === existing ? { ...action.toast, open: true } : item
    );
  }

  const target: IToastItem | undefined = items.find((item: IToastItem): boolean => item.id === action.id && item.open);
  if (target === undefined) {
    return items;
  }

  if (action.kind === "update") {
    return items.map((item: IToastItem): IToastItem => (item === target ? { ...item, ...action.patch } : item));
  }

  return items.map((item: IToastItem): IToastItem =>
    item === target ? { ...item, open: false, closedAt: now } : item
  );
};

/** Open toasts, newest first. */
export const openToastIdsNewestFirst = (items: IToastItem[]): ToastId[] =>
  items
    .filter((item: IToastItem): boolean => item.open)
    .map((item: IToastItem): ToastId => item.id)
    .reverse();

/**
 * Where each open toast sits. Collapsed: the newest is in front at its own height, up to two older
 * ones peek out behind it cut to its height, the rest are hidden. Expanded (pointer or focus on the
 * stack): the visible ones stand in a column, each lifted by the heights of the newer ones.
 */
export const computeToastStack = (
  openIdsNewestFirst: ToastId[],
  heights: Record<ToastId, number>,
  isExpanded: boolean
): IToastStack => {
  const visible: ToastId[] = openIdsNewestFirst.slice(0, TOAST_VISIBLE_LIMIT);
  const frontHeight: number | undefined = visible.length > 0 ? heights[visible[0]] : undefined;
  const layouts: Map<ToastId, IToastStackLayout> = new Map<ToastId, IToastStackLayout>();

  let offset: number = 0;
  openIdsNewestFirst.forEach((id: ToastId, index: number): void => {
    const ownHeight: number | undefined = heights[id];
    const isHidden: boolean = index >= TOAST_VISIBLE_LIMIT;
    layouts.set(id, {
      index,
      isHidden,
      peekSteps: Math.min(index, TOAST_VISIBLE_LIMIT - 1),
      offset,
      height: isExpanded || index === 0 ? ownHeight : frontHeight
    });
    if (!isHidden) {
      offset += ownHeight ?? 0;
    }
  });

  if (isExpanded) {
    return {
      layouts,
      contentHeight: visible.reduce((sum: number, id: ToastId): number => sum + (heights[id] ?? 0), 0),
      gapCount: Math.max(0, visible.length - 1)
    };
  }

  return { layouts, contentHeight: frontHeight ?? 0, gapCount: Math.max(0, visible.length - 1) };
};
